// 模型测试组件
import React, { useState, useEffect } from 'react';
import { Play, DollarSign, Key, Settings, Upload, FileText, Check } from 'lucide-react';
import { api } from '../lib/api';
import CustomSelect from './CustomSelect';

// 添加滑块样式
const sliderStyles = `
  .slider::-webkit-slider-thumb {
    appearance: none;
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: 2px solid white;
  }
  
  .slider::-moz-range-thumb {
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .slider::-webkit-slider-track {
    height: 8px;
    border-radius: 4px;
    background: #e5e7eb;
  }
  
  .slider::-moz-range-track {
    height: 8px;
    border-radius: 4px;
    background: #e5e7eb;
  }
`;

// 注入样式
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = sliderStyles;
  document.head.appendChild(styleElement);
}

interface Model {
  id: number;
  name: string;
  slug: string;
  provider_model_name: string;
  description: string;
  status: string;
  parameters: any;
  tags: string[];
  example_template?: string;
  pricing: {
    display_price: string;
    pricing_type: string;
    description: string;
  };
}

interface APIKey {
  id: string;
  api_key?: string;
  key_name: string;
  is_active: boolean;
  masked_key?: string;
}

interface ModelTestingProps {
  model: Model;
  category?: any;
  onBalanceUpdate?: (newBalance: number) => void;
}

interface ParsedParameter {
  key: string;
  value: any;
  type: 'number' | 'string' | 'boolean' | 'array' | 'object';
  inputType: 'slider' | 'select' | 'textarea' | 'input' | 'file' | 'checkbox';
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
}

// 解析结果接口
interface ParseResult {
  endpoint: string;
  parameters: Record<string, any>;
  contentType: string;
  requiresFile?: boolean;
}

const ModelTesting: React.FC<ModelTestingProps> = ({ model, onBalanceUpdate }) => {
  const [balance, setBalance] = useState<number>(0);
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [selectedKeyId, setSelectedKeyId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  // 模型参数 - 根据模型动态设置
  const [parameters, setParameters] = useState<any>({});

  // 解析结果

  const [configLoading, setConfigLoading] = useState(true);
  const [tryItConfig, setTryItConfig] = useState<any>(null);
const [configError, setConfigError] = useState<string>('');

  // 输入内容
  const [prompt, setPrompt] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

 

  useEffect(() => {
    fetchUserData();
    fetchTryItConfig();
  }, [model]);

  // 从配置表加载参数
useEffect(() => {
  if (tryItConfig && tryItConfig.parameters) {
    const defaultParams: any = {};
    Object.keys(tryItConfig.parameters).forEach(key => {
      const param = tryItConfig.parameters[key];
      defaultParams[key] = param.default;
    });
    setParameters(defaultParams);
  }
}, [tryItConfig]);

  // 获取模型的Try It配置
const fetchTryItConfig = async () => {
  try {
    setConfigLoading(true);
    setConfigError('');

    const response = await api.get(`/api/models/${model.id}/try-it-config`);
    
    if (response.data && response.data.success) {
      setTryItConfig(response.data.data);
    } else {
      setConfigError('No Try It configuration found for this model');
    }
  } catch (err: any) {
      setConfigError('Failed to load Try It configuration');
    } finally {
    setConfigLoading(false);
  }
};

const getParameterMin = (key: string) => {
  if (key === 'temperature') return 0;
  if (key.includes('token') || key === 'max_tokens') return 1;
  if (key === 'top_p') return 0;
  if (key === 'top_k') return 1;
  if (key.includes('penalty')) return -2;
  if (key === 'n') return 1;
  if (key === 'steps') return 1;
  if (key === 'cfg_scale') return 1;
  if (key === 'speed') return 0.25;
  return 0;
};

const getParameterMax = (key: string) => {
  if (key === 'temperature') return 2;
  if (key.includes('token') || key === 'max_tokens') return 4000;
  if (key === 'top_p') return 1;
  if (key === 'top_k') return 100;
  if (key.includes('penalty')) return 2;
  if (key === 'n') return 4;
  if (key === 'steps') return 100;
  if (key === 'cfg_scale') return 20;
  if (key === 'speed') return 4;
  return 100;
};

const getParameterStep = (key: string) => {
  if (key === 'temperature' || key === 'top_p' || key.includes('penalty')) return 0.1;
  if (key === 'cfg_scale') return 0.5;
  if (key === 'speed') return 0.25;
  return 1;
};

const getParameterOptions = (key: string): string[] => {
  if (key === 'size') return ['256x256', '512x512', '1024x1024', '1792x1024', '1024x1792'];
  if (key === 'quality') return ['standard', 'hd'];
  if (key === 'style') return ['photographic', 'digital-art', 'cinematic', 'anime', 'vivid', 'natural'];
  if (key === 'voice') return ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
  return [];
};

  const fetchUserData = async () => {
    try {
      // 获取用户余额
      const balanceResponse = await api.get('/api/user/info');
      setBalance(balanceResponse.data.balance);

      // 获取API Keys
      const keysResponse = await api.get('/api/keys');
      const activeKeys = keysResponse.data.data.filter((key: APIKey) => key.is_active);
      setApiKeys(activeKeys);

      // 自动选择第一个可用的Key
      if (activeKeys.length > 0) {
        setSelectedKeyId(activeKeys[0].id);
      }
    } catch (err: any) {
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleParameterChange = (key: string, value: number | string | boolean | File | null) => {
    setParameters((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  // 检查当前模型是否需要prompt参数
const requiresPrompt = (): boolean => {
  if (tryItConfig) {
    return tryItConfig.requires_prompt;
  }
  return true; // 默认需要prompt
};

  // ModelTesting.tsx 中的 runTest 函数修复版本
  // 这个函数负责执行模型试用功能

  const runTest = async () => {
    // 只有当模型需要prompt时才验证prompt
    if (requiresPrompt() && !prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    if (!selectedKeyId) {
      setError('Please select an API Key');
      return;
    }

    setTesting(true);
    setError('');
    setResult(null);

    try {
      // 从cURL示例中解析出的完整接口URL
      const apiUrl = tryItConfig.api_endpoint;
      if (!apiUrl) {
        setError('Unable to parse model API endpoint URL');
        return;
      }

      // 获取选中的API密钥对象
      const selectedKey = apiKeys.find(key => key.id === selectedKeyId);
      if (!selectedKey || !selectedKey.api_key) {
        setError('Please select a valid API key');
        return;
      }

      // 检查解析结果
      if (!tryItConfig || !tryItConfig.api_endpoint) {
        setError('Model configuration is being parsed, please try again later');
        return;
      }

      // 根据URL判断请求格式类型（使用之前的成功方案）
      const isFormDataRequest = tryItConfig.content_type === 'multipart/form-data';
      const actualApiUrl = tryItConfig.api_endpoint;
      


      let response;

      if (isFormDataRequest) {
        // Form-data格式请求 (图生图、语音识别、3D生成、视频生成等)

        response = await handleFormDataRequest(actualApiUrl, selectedKey.api_key);
      } else {
        // JSON格式请求 (文本生成、文生图、向量化等)

        response = await handleJsonRequest(actualApiUrl, selectedKey.api_key);
      }

      // 处理响应结果
      if (response.data) {
        setResult(response.data);

        // API调用成功后立即获取最新余额
        if (onBalanceUpdate) {
          try {
            const balanceResponse = await api.get('/api/user/info');
            const newBalance = balanceResponse.data.balance;
            onBalanceUpdate(newBalance);
          } catch (err) {
            // Failed to fetch updated balance
          }
        }
      } else {
        setError('未收到有效响应数据');
      }

    } catch (err: any) {

      let errorMessage = '';

      // 处理不同类型的错误
      if (err.response) {
        const status = err.response.status;
        const errorData = err.response.data;

        if (status === 402) {
          errorMessage = 'Insufficient balance. Please add funds to continue.';
        } else if (status === 401) {
          errorMessage = 'Invalid or expired API key. Please check your key.';
        } else if (status === 400) {
          errorMessage = errorData?.error?.message || errorData?.message || 'Bad request. Please check your parameters.';
        } else if (status === 500) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else {
          errorMessage = errorData?.error?.message || errorData?.message || `HTTP ${status}: Request failed`;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }

      setError(errorMessage);

    } finally {
      setTesting(false);
    }
  };

  // 根据接口URL判断是否使用Form-data格式
  

  // 处理Form-data格式的请求
  const handleFormDataRequest = async (apiUrl: string, apiKey: string) => {
    const formData = new FormData();

    // 添加基础参数
    formData.append('model', model.slug);
    
    // 只有需要prompt的接口才发送prompt
if (requiresPrompt()) {
    if (prompt.trim()) {
        formData.append('prompt', prompt.trim());
    } else {
        throw new Error('Prompt is required for this model');
    }
} 
// 如果不需要prompt，即使用户输入了也不发送

    // 添加文件
    if (uploadedFile) {
      const fileFieldName = tryItConfig.file_field_name || 'file';
      formData.append(fileFieldName, uploadedFile);
    } else if (requiresFileForCurrentModel()) {
      throw new Error('This model requires file upload, please select a file first');
    }

    // 添加其他参数
    Object.entries(parameters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        formData.append(key, value.toString());
      }
    });



    // 准备请求头
    const requestHeaders = {
      'x-api-key': apiKey,
    };
    
    try {

      
  // 使用axios发送FormData（与JSON请求保持一致）
  const response = await api.post(apiUrl.replace(/^https?:\/\/[^\/]+/, ''), formData, {
    headers: {
      'x-api-key': apiKey,
      // axios会自动处理FormData的Content-Type
    },
  });

  return response;
} catch (err: any) {
  throw err;
}


  };

  // 从模板构建请求体
const buildRequestFromTemplate = (template: any, userInput: string, modelSlug: string, params: any) => {
  let templateStr = JSON.stringify(template);

  templateStr = templateStr.replace(/\{\{USER_INPUT\}\}/g, userInput);
  templateStr = templateStr.replace(/\{\{MODEL_SLUG\}\}/g, modelSlug);
  
  Object.keys(params).forEach(key => {
  const placeholder = `"{{${key.toUpperCase()}}}"`; // 注意这里加了引号
  const value = params[key];
  const replacement = JSON.stringify(value); // 使用JSON.stringify自动处理类型
  templateStr = templateStr.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacement);
});

  const result = JSON.parse(templateStr);

  return result;
};

  // 处理JSON格式的请求
  const handleJsonRequest = async (apiUrl: string, apiKey: string) => {
    
    // 使用request_template构建请求体
let requestData: any;

if (tryItConfig.request_template) {
  // 从模板构建请求体
  requestData = buildRequestFromTemplate(tryItConfig.request_template, prompt, model.slug, parameters);
} else {
  // 如果没有模板，使用简单格式
  requestData = {
    model: model.slug,
    prompt: prompt,
    ...parameters
  };
}
    // 清理空值参数 - 移除所有空值参数，不发送空字符串
    Object.keys(requestData).forEach(key => {
      const value = requestData[key];
      
      // 删除所有空值：null、undefined、空字符串
      // 但保留prompt字段，即使为空（因为prompt是必需字段）
      if (key !== 'prompt' && (value === null || value === undefined || value === '')) {
        delete requestData[key];
      }
    });
    


    // 检查是否启用流式输出
const isStreaming = requestData.stream === true;

if (isStreaming) {
  // 流式请求处理
  return await handleStreamingRequest(apiUrl, apiKey, requestData);
} else {
  // 普通请求处理
  const response = await api.post(apiUrl.replace(/^https?:\/\/[^\/]+/, ''), requestData, {
    headers: {
      'x-api-key': apiKey,
      'Content-Type': 'application/json',
    },
  });
  return response;
}
  };

  // 处理流式请求
const handleStreamingRequest = async (apiUrl: string, apiKey: string, requestData: any) => {
  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://p.qmtju.com';
const fullUrl = baseUrl + apiUrl.replace(/^https?:\/\/[^\/]+/, '');
const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'x-api-key': apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Stream reader not available');
    }

    let streamedContent = '';
    setResult({ type: 'streaming', content: '', isComplete: false });

    const decoder = new TextDecoder();
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        setResult((prev: any) => ({ ...prev, isComplete: true }));
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
const lines = chunk.split('\n');

for (const line of lines) {
  if (line.trim() === '') continue;
  if (line.startsWith('data: ')) {
    const data = line.slice(6);
    if (data === '[DONE]') {
      setResult((prev: any) => ({ ...prev, isComplete: true }));
      continue;
    }

    try {
      const parsed = JSON.parse(data);
      const content = parsed.choices?.[0]?.delta?.content || '';
      if (content) {
        streamedContent += content;
        setResult((prev: any) => ({ ...prev, content: streamedContent }));
      }
    } catch (e) {
      // Failed to parse streaming data
    }
  }
}
    }

    // 返回兼容格式的结果
    return {
      data: {
        type: 'streaming',
        choices: [{
          message: {
            content: streamedContent
          }
        }],
        isComplete: true
      }
    };

  } catch (error) {
        throw error;
      }
};

  // 获取文件字段名
  



  // 判断是否应该显示文件上传
  const shouldShowFileUpload = (): boolean => {
  if (!tryItConfig || !tryItConfig.api_endpoint) return false;
  return tryItConfig.requires_file;
};

  // 判断当前模型是否必须上传文件
  const requiresFileForCurrentModel = (): boolean => {
  if (!tryItConfig) return false;
  return tryItConfig.requires_file;
};

  // 获取文件上传标签文本
  const getFileUploadLabel = (): string => {
  if (!tryItConfig || !tryItConfig.requires_file) return 'Upload File';

  if (tryItConfig.api_endpoint.includes('/audio/transcriptions')) {
    return 'Audio File';
  } else if (tryItConfig.api_endpoint.includes('/images/edits') || tryItConfig.api_endpoint.includes('/images/variations')) {
    return 'Image File';
  } else if (tryItConfig.api_endpoint.includes('/3d/generations')) {
    return 'Reference Image';
  } else if (tryItConfig.api_endpoint.includes('/videos/generations')) {
    return 'Reference Image (Optional)';
  }

  return 'Upload File';
};

  // 获取接受的文件类型
  const getAcceptedFileTypes = (): string => {
  if (!tryItConfig || !tryItConfig.requires_file) return '*/*';
  
  return tryItConfig.accepted_file_types || '*/*';
};

  // 获取文件类型描述文本
  const getFileTypeText = (): string => {
  if (!tryItConfig || !tryItConfig.requires_file) return 'file';

  if (tryItConfig.api_endpoint.includes('/audio/transcriptions')) {
    return 'audio file';
  } else if (tryItConfig.api_endpoint.includes('/images/') || tryItConfig.api_endpoint.includes('/3d/') || tryItConfig.api_endpoint.includes('/videos/')) {
    return 'image file';
  }

  return 'file';
};

  // 获取文件上传提示信息
  const getFileUploadHint = (): string => {
  if (!tryItConfig || !tryItConfig.requires_file) return '';

  if (tryItConfig.api_endpoint.includes('/audio/transcriptions')) {
    return 'Supported formats: MP3, WAV, M4A, FLAC, OGG';
  } else if (tryItConfig.api_endpoint.includes('/images/edits')) {
    return 'Upload the image you want to edit';
  } else if (tryItConfig.api_endpoint.includes('/images/variations')) {
    return 'Upload the image to create variations';
  } else if (tryItConfig.api_endpoint.includes('/images/mattings')) {
    return 'Upload the image to remove background and extract main subject';
  } else if (tryItConfig.api_endpoint.includes('/images/upscaling')) {
    return 'Upload the image to enhance resolution and quality';
  } else if (tryItConfig.api_endpoint.includes('/images/style-migration')) {
    return 'Upload the image to apply artistic style transfer';
  } else if (tryItConfig.api_endpoint.includes('/3d/generations')) {
    return 'Upload a reference image for 3D generation';
  } else if (tryItConfig.api_endpoint.includes('/videos/generations')) {
    return 'Upload an image for image-to-video generation (optional for text-to-video)';
  }

  return '';
};

  // 渲染不同类型的结果
  const renderResult = (result: any, apiUrl: string) => {
    if (!result) return null;

    // 流式结果显示
if (result.type === 'streaming') {
  return (
    <div className="space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-sm font-medium text-blue-900">Generated Text (Streaming)</h5>
          {!result.isComplete && (
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
              <span className="text-xs text-blue-600">Generating...</span>
            </div>
          )}
        </div>
        <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
          {result.content}
          {!result.isComplete && (
            <span className="inline-block w-2 h-4 bg-blue-600 animate-pulse ml-1"></span>
          )}
        </div>
      </div>
    </div>
  );
}

    // 文本生成结果 - 支持所有chat/completions接口
if (result.choices?.[0]?.message?.content) {
  const content = result.choices[0].message.content;
  return (
    <div className="space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="text-sm font-medium text-blue-900 mb-2">AI Response</h5>
        <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
          {content}
        </div>
      </div>
    </div>
  );
}

    // 图像生成结果 - 包含编辑和变体
    if ((apiUrl.includes('/images/') || apiUrl.includes('/edits') || apiUrl.includes('/variations')) && result.data && Array.isArray(result.data)) {
      const images = result.data || [];
      return (
        <div className="space-y-3">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-900 mb-3">Generated Images</h5>
            <div className="grid grid-cols-1 gap-4">
              {images.map((image: any, index: number) => (
                <div key={index} className="space-y-2">
                  {image.url ? (
                    <img
                      src={image.url}
                      alt={`Generated image ${index + 1}`}
                      className="w-full max-w-md mx-auto rounded-lg shadow-sm border"

                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full max-w-md mx-auto h-48 bg-gray-100 rounded-lg border flex items-center justify-center">
                      <p className="text-gray-500 text-sm">No image URL received</p>
                    </div>
                  )}
                  {image.revised_prompt && (
                    <p className="text-xs text-gray-600 italic">Revised: {image.revised_prompt}</p>
                  )}
                  {image.revised_prompt && (
                    <p className="text-xs text-gray-600 italic">Revised: {image.revised_prompt}</p>
                  )}
                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Image URL:</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(image.url);
                          // 可以添加复制成功提示
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="mt-1 p-2 bg-white border rounded text-xs font-mono text-gray-700 break-all">
                      {image.url}
                    </div>
                  </div>
                </div>

              ))}
            </div>

          </div>

        </div>
      );
    }

    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Response</h5>
        <div className="text-xs text-gray-600">
          This model type doesn't have a custom result display yet. Showing raw response:
        </div>
        <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono leading-relaxed mt-2">
          {JSON.stringify(result, null, 2)}
        </pre>
      </div>
    );
  };



  const clearTest = () => {
    setPrompt('');
    setUploadedFile(null);
    setResult(null);
    setError('');
  };

  if (loading || configLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">
          {loading ? 'Loading user data...' : 'Loading model configuration...'}
        </span>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* 主要内容区域 */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左侧：配置面板 */}
        <div className="lg:col-span-1 space-y-4">
          {/* API Key 选择 */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center space-x-2 mb-3">
              <Key className="h-4 w-4 text-gray-600" />
              <h4 className="text-sm font-semibold text-gray-900">API Key</h4>
            </div>
            <CustomSelect
              value={selectedKeyId}
              onChange={setSelectedKeyId}
              placeholder="Choose API Key"
              options={[
                { value: '', label: 'Choose API Key' },
                ...apiKeys.map((key) => ({
                  value: key.id,
                  label: `${key.key_name} ${key.masked_key ? `(${key.masked_key})` : ''}`
                }))
              ]}
            />
          </div>

          {/* 参数配置 */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center space-x-2 mb-4">
              <Settings className="h-4 w-4 text-gray-600" />
              <h4 className="text-sm font-semibold text-gray-900">Parameters</h4>
            </div>
            <div className="space-y-4">
              
              {/* 动态渲染参数 - 基于真实cURL示例 */}
              
              {Object.keys(parameters).length > 0 ? (
                Object.keys(parameters).map(key => {
                  const value = parameters[key];
                  const paramInfo = tryItConfig.parameters[key] || { type: 'string', default: value };

                  // 渲染滑块参数
                  if (paramInfo.type === 'number' && (key === 'temperature' || key.includes('token') || key === 'top_p' || key === 'top_k' || key.includes('penalty') || key === 'n' || key === 'steps' || key === 'cfg_scale' || key === 'speed')) {
                    return (
                      <div key={key}>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-xs font-medium text-gray-700 capitalize">
                            {key.replace(/_/g, ' ')}
                          </label>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-mono">
                            {value}
                          </span>
                        </div>
                        <input
                          type="range"
                          min={getParameterMin(key)}
max={getParameterMax(key)}
step={getParameterStep(key)}
                          value={value}
                          onChange={(e) => {
                            const newValue = getParameterStep(key) < 1
  ? parseFloat(e.target.value)
  : parseInt(e.target.value);
                            handleParameterChange(key, newValue);
                          }}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                      </div>
                    );
                  }

                  // 渲染下拉选择参数
                  if (paramInfo.type === 'string' && (key === 'size' || key === 'quality' || key === 'style' || key === 'voice')) {
                    return (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-2 capitalize">
                          {key.replace(/_/g, ' ')}
                        </label>
                        <CustomSelect
                          value={value || ''}
                          onChange={(newValue) => handleParameterChange(key, newValue)}
                          options={getParameterOptions(key).map(option => ({
                            value: option,
                            label: option.charAt(0).toUpperCase() + option.slice(1)
                          }))}
                        />
                      </div>
                    );
                  }

                  // 渲染文本区域参数
                  if (paramInfo.type === 'string' && (key.includes('prompt') || key.includes('content') || (typeof value === 'string' && value.length > 50))) {
                    return (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-2 capitalize">
                          {key.replace(/_/g, ' ')}
                        </label>
                        <textarea
                          value={value}
                          onChange={(e) => handleParameterChange(key, e.target.value)}
                          className="w-full h-20 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          placeholder={`Enter ${key.replace(/_/g, ' ')}...`}
                        />
                      </div>
                    );
                  }

                  // 渲染复选框参数
                  if (paramInfo.type === 'boolean') {
                    return (
                      <div key={key} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={value === true}
                          onChange={(e) => handleParameterChange(key, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label className="text-xs font-medium text-gray-700 capitalize">
                          {key.replace(/_/g, ' ')}
                        </label>
                      </div>
                    );
                  }

                  // 文件上传参数已移除，使用右侧统一的文件上传组件

                  // 渲染普通输入框参数
                  return (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-700 mb-2 capitalize">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <input
                        type={paramInfo.type === 'number' ? 'number' : 'text'}
                        value={value}
                        onChange={(e) => {
                          const newValue = paramInfo.type === 'number'
  ? parseFloat(e.target.value) || 0
  : e.target.value;
                          handleParameterChange(key, newValue);
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Enter ${key.replace(/_/g, ' ')}...`}
                      />
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No configurable parameters found</p>
                  <p className="text-xs text-gray-400 mt-1">This model may not have a cURL example template</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 右侧：输入和结果区域 */}
        <div className="lg:col-span-2 space-y-4">
          {/* 输入区域 */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-gray-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Input</h4>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={clearTest}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={runTest}
                    disabled={testing || (requiresPrompt() && !prompt.trim()) || !selectedKeyId}
                    className="flex items-center px-4 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium shadow-sm transition-all"
                  >
                    <Play className="h-3 w-3 mr-1.5" />
                    {testing ? 'Running...' : 'Run Test'}
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {requiresPrompt() && (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-2">
      Prompt
    </label>
    <textarea
      value={prompt}
      onChange={(e) => setPrompt(e.target.value)}
      placeholder="Enter your prompt here..."
      className="w-full h-24 px-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-gray-300 resize-none"
      style={{ 
        resize: 'none',
        outline: 'none',
        boxShadow: 'none'
      }}
    />
  </div>
)}

              {/* Negative Prompt for image models */}
              {parameters.negative_prompt !== undefined && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Negative Prompt</label>
                  <textarea
                    value={parameters.negative_prompt || ''}
                    onChange={(e) => handleParameterChange('negative_prompt', e.target.value)}
                    placeholder="Enter what you don't want in the image..."
                    className="w-full h-20 px-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
              )}

              {/* 智能文件上传 - 根据模型类型判断是否显示 */}
              {shouldShowFileUpload() && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    {getFileUploadLabel()}
                    {requiresFileForCurrentModel() && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      accept={getAcceptedFileTypes()}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center space-y-2"
                    >
                      {uploadedFile ? (
                        <>
                          <div className="w-20 h-20 border-2 border-green-300 rounded-lg overflow-hidden bg-white">
                            {uploadedFile.type.startsWith('image/') ? (
                              <img
                                src={URL.createObjectURL(uploadedFile)}
                                alt="Uploaded preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-green-100 flex items-center justify-center">
                                <Check className="h-6 w-6 text-green-600" />
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-700 text-center">
                            <p className="font-medium">{uploadedFile.name}</p>
                            <p className="text-gray-500">Size: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setUploadedFile(null)}
                            className="text-xs text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                          >
                            Remove file
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <Upload className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="text-xs text-gray-600">
                            <p className="font-medium">Click to upload {getFileTypeText()}</p>
                            <p className="text-gray-400">or drag and drop</p>
                          </div>
                        </>
                      )}
                    </label>
                  </div>
                  {getFileUploadHint() && (
                    <p className="text-xs text-gray-500 mt-1">{getFileUploadHint()}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 错误显示 */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* 结果显示 */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm min-h-[200px]">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">Result</h4>
                {result?.usage && (
                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-mono">
                      {result.usage.total_tokens} tokens
                    </span>
                    {result.usage.prompt_tokens && (
                      <span className="text-gray-500">
                        {result.usage.prompt_tokens} in + {result.usage.completion_tokens} out
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="p-4">
              {result ? (
                renderResult(result, tryItConfig?.api_endpoint || '')
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Play className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Ready to Test</p>
                  <p className="text-xs text-gray-500">Enter your prompt and click "Run Test" to see the results here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelTesting;